// F24View.jsx — amm-v3 (A7, ondata 34 — SETTIMA e ULTIMA ondata del cantiere
// AMM-v3): la vista "Pagamenti F24" (pagina 'f24' — la LISTA dei modelli
// F24/versamenti) in React, flag-gated (shell_amm_v3_f24, default OFF).
//
// Replica 1:1 renderF24 di admin-module.js r.6883-6985: h1 con tooltip tecnico,
// 4 bottoni header (Esporta CSV, Importa quietanza AI, Ravvedimento, + Nuovo F24),
// 3 KPI (registrati/versato/da-pagare), barra filtri (testo + anno + mese + 5 chip
// stato), tabella (Data · Riferimento+badge tipo · Righe · A debito · Crediti ·
// Saldo · Stato+badge "senza PDF" · elimina-admin). Riga cliccabile ⇒ dettaglio V1.
//
// CONFINE (dichiarato, come le rate editabili in A5): IL WIZARD AI di import
// quietanze (openImportQuietanza) resta V1 — è troppo accoppiato (multi-file,
// multi-quietanza, riversaStepCorrente che legge il DOM) e già SICURO su tutte
// le invarianti d'import (anti-perdita-dati al cambio passo/click-fuori,
// busy() anti-doppio-click su "Importa", errore rumoroso coi dati intatti,
// guardia _importata anti-doppia-registrazione — census a7-01 §2). Con esso
// restano V1 anche editF24 (+Nuovo/modifica), openRavvedimento, openF24
// (dettaglio) e l'elimina (delSafe+messaggioCollegamenti). Il v3 li DELEGA
// passando il PROPRIO onSaved (refresh v3); openF24 si auto-gestisce ⇒ mai
// renderF24 diretto (niente bypass/interceptor — census a7-01 §1).
//
// LOGIC SIMULATION (stati e sequenze SBAGLIATE che finiscono bene):
//   load ok            → KPI + filtri + tabella
//   filtro testo/anno/mese/chip → ri-filtra client-side (delega f24Matches* v1)
//   click riga         → openF24(id) [modale v1, la lista NON si auto-aggiorna: parità]
//   elimina            → collegamenti + confirm INFORMATO → delSafe(remove) → toast+reload
//   doppio-click "+ Nuovo"/"Importa"/"Ravvedimento"/"Esporta"/elimina → 1 azione (busyRef, D-A7.1)
//   reload FALLITO con righe a schermo → banner + Riprova, righe conservate (D-A7.2)
//   reload FALLITO senza righe        → StatoBox errore + Riprova (DataTable)
//
// DEVIAZIONI DICHIARATE:
//  D-A7.1 doppio click su un'azione header/elimina = 1 sola azione (guardia ref).
//         Il v1 NON guarda (r.6947-6949 aprono un modale per click): miglioria.
//  D-A7.2 caricamento fallito con righe presenti ⇒ banner rumoroso + Riprova, dati
//         conservati (famiglia D-A5.3). Il v1 in questo caso lascerebbe la pagina
//         rotta (renderF24 è un solo fetch senza catch).

import React from 'react';
import { DataTable } from '../ui/DataTable.jsx';
import { AM_NEW_TEXTS } from './api.js';
import {
  F24_TEXTS, MESI_OPT, STATI_OPT, kpiDefs, formatData, anniDisponibili,
  tipoBadgeLabel, mostraTipoBadge, emptyMessage, filtraF24,
  readF24, apriNuovoF24, apriRavvedimento, apriImportQuietanza, apriDettaglio,
  messaggioCollegamentiF24, eliminaF24, esportaCsv,
} from './f24-api.js';

const BADGE_TIPO = { background: '#fef3c7', color: '#92400e' };

function V1Html({ html }) {
  if (!html) return null;
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

export function F24View({ win, tick }) {
  const A = win.AMM;
  const [dati, setDati] = React.useState({ loading: true, error: null, list: null });
  const [reloadN, setReloadN] = React.useState(0);
  const [q, setQ] = React.useState('');
  const [anno, setAnno] = React.useState('');
  const [mese, setMese] = React.useState('');
  const [stato, setStato] = React.useState('');
  const busyRef = React.useRef({}); // guardia per-azione (D-A7.1)

  React.useEffect(() => {
    let vivo = true;
    setDati((d) => ({ ...d, loading: true, error: null }));
    readF24(win).then(
      (list) => { if (vivo) setDati({ loading: false, error: null, list }); },
      (e) => { if (vivo) setDati((d) => ({ ...d, loading: false, error: e })); }
    );
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [win, tick, reloadN]);

  const list = dati.list;
  const reload = () => setReloadN((n) => n + 1);

  // apertura modale delegato con guardia doppio-click: il modale può chiudersi
  // senza salvare ⇒ il ref si rilascia anche a timeout (parità RateizzazioniView).
  const apriGuarded = (k, fn) => {
    if (busyRef.current[k]) return;
    busyRef.current[k] = true;
    try { fn(() => { busyRef.current[k] = false; reload(); }); }
    finally { setTimeout(() => { busyRef.current[k] = false; }, 400); }
  };
  const onNuovo = () => apriGuarded('nuovo', (onSaved) => apriNuovoF24(win, onSaved));
  const onRavvedimento = () => apriGuarded('rav', (onSaved) => apriRavvedimento(win, onSaved));
  const onImport = () => apriGuarded('quiet', (onSaved) => apriImportQuietanza(win, onSaved));

  const onExport = async () => {
    if (busyRef.current.export) return;
    busyRef.current.export = true;
    const annoExp = anno || new Date().getFullYear();
    try {
      await esportaCsv(win, annoExp);
      if (!anno) A.toast(F24_TEXTS.exportToastPre + annoExp + F24_TEXTS.exportToastPost);
    } catch (e) {
      A.toast(F24_TEXTS.exportFallito + e.message);
    } finally {
      busyRef.current.export = false;
    }
  };

  const onDelete = async (id) => {
    const dk = 'd' + id;
    if (busyRef.current[dk]) return;
    busyRef.current[dk] = true;
    try {
      const extra = await messaggioCollegamentiF24(win, id);
      if (!win.confirm(F24_TEXTS.confirmElimina + (extra ? '\n\n' + extra : ''))) { busyRef.current[dk] = false; return; }
      eliminaF24(win, id, () => { A.toast(F24_TEXTS.toastEliminato); busyRef.current[dk] = false; reload(); })
        .catch(() => { busyRef.current[dk] = false; });
    } catch (e) {
      busyRef.current[dk] = false;
    }
  };

  const kpis = kpiDefs(list || [], A.fmtEur2);
  const anni = anniDisponibili(list || []);
  const filtered = filtraF24(win, list || [], q.trim(), anno, mese, stato);

  const columns = [
    { key: 'data_versamento', label: F24_TEXTS.colData, render: (f) => <span className="font-medium">{formatData(f.data_versamento)}</span> },
    {
      key: 'riferimento', label: F24_TEXTS.colRiferimento,
      render: (f) => (
        <span>
          {f.descrizione || f.numero || F24_TEXTS.dash}
          {mostraTipoBadge(f.tipo) ? <>{' '}<span className="amm-badge" style={BADGE_TIPO}>{tipoBadgeLabel(f.tipo)}</span></> : null}
        </span>
      ),
    },
    { key: 'n_righe', label: F24_TEXTS.colRighe, render: (f) => <span className="text-slate-500">{f.n_righe || 0}</span> },
    { key: 'tot_debito', label: F24_TEXTS.colDebito, render: (f) => <span className="tabular-nums">{A.fmtEur2(f.tot_debito)}</span> },
    { key: 'tot_credito', label: F24_TEXTS.colCrediti, render: (f) => <span className="tabular-nums text-slate-500">{A.fmtEur2(f.tot_credito)}</span> },
    { key: 'saldo', label: F24_TEXTS.colSaldo, render: (f) => <span className="tabular-nums font-semibold">{A.fmtEur2(f.saldo)}</span> },
    {
      key: 'stato', label: F24_TEXTS.colStato,
      render: (f) => (
        <span>
          <V1Html html={A.f24StatoBadge(f.stato)} />
          {f.senza_pdf ? <>{' '}<span className="amm-badge" style={BADGE_TIPO} title={F24_TEXTS.senzaPdfTitle}>{'⚠ ' + F24_TEXTS.senzaPdf}</span></> : null}
        </span>
      ),
    },
    {
      key: '__act', label: '', align: 'right', filter: false,
      render: (f) => (A.isAdmin()
        ? <button type="button" className="amm-f24-del text-rose-600 hover:text-rose-800 text-xs" data-id={f.id} onClick={() => onDelete(f.id)}>{F24_TEXTS.elimina}</button>
        : null),
    },
  ];

  const chipBase = 'amm-fchip amm-f24-stato-chip cursor-pointer text-xs px-3 py-1.5 rounded-full border';

  return (
    <div data-amm-v3="f24">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold" title={F24_TEXTS.titleTooltip}>{F24_TEXTS.title}</h1>
        <div className="flex gap-2">
          <button id="amm-f24-export" type="button" className="bg-slate-200 hover:bg-slate-300 text-slate-800 text-sm px-3 py-2 rounded font-medium" onClick={onExport}>{F24_TEXTS.exportCsv}</button>
          <button id="amm-f24-quiet" type="button" className="bg-violet-600 hover:bg-violet-500 text-white text-sm px-3 py-2 rounded font-medium" onClick={onImport}>{F24_TEXTS.importQuietanza}</button>
          <button id="amm-f24-rav" type="button" className="bg-amber-100 hover:bg-amber-200 text-amber-800 text-sm px-3 py-2 rounded font-medium" onClick={onRavvedimento}>{F24_TEXTS.ravvedimento}</button>
          <button id="amm-f24-new" type="button" className="bg-cyan-700 hover:bg-cyan-600 text-white text-sm px-3 py-2 rounded font-medium" onClick={onNuovo}>{F24_TEXTS.nuovoF24}</button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        {kpis.map((k) => (
          <div key={k.label} className="bg-white border rounded-lg p-4">
            <div className="text-[10px] uppercase tracking-wide text-slate-400">{k.label}</div>
            <div className="text-lg font-semibold mt-0.5">{k.val}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-3 items-center">
        <input
          id="amm-f24-filtro" type="text" placeholder={F24_TEXTS.filterPlaceholder}
          className="border rounded px-3 py-1.5 text-sm w-64"
          value={q} onChange={(e) => setQ(e.target.value)}
        />
        <select id="amm-f24-anno" className="border rounded px-2 py-1.5 text-sm" value={anno} onChange={(e) => setAnno(e.target.value)}>
          <option value="">{F24_TEXTS.tuttiGliAnni}</option>
          {anni.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select id="amm-f24-mese" className="border rounded px-2 py-1.5 text-sm" value={mese} onChange={(e) => setMese(e.target.value)}>
          <option value="">{F24_TEXTS.tuttiIMesi}</option>
          {MESI_OPT.map((m) => <option key={m[0]} value={m[0]}>{m[1]}</option>)}
        </select>
        {STATI_OPT.map((s) => {
          const on = stato === s;
          return (
            <span
              key={s} data-stato={s} className={chipBase + (on ? '' : ' bg-white text-slate-600 border-slate-200')}
              style={on ? { background: '#ecfeff', color: '#0e7490', borderColor: '#a5f3fc' } : undefined}
              onClick={() => setStato((cur) => (cur === s ? '' : s))}
            >{s}</span>
          );
        })}
      </div>

      {/* D-A7.2: errore con righe presenti ⇒ banner + Riprova, dati conservati */}
      {dati.error && list && list.length ? (
        <div role="alert" className="mb-3 px-4 py-2.5 rounded-lg text-sm font-medium flex items-center justify-between gap-3" style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}>
          <span>{AM_NEW_TEXTS.erroreRicarica + (dati.error.message || String(dati.error))}</span>
          <button type="button" style={{ background: '#0e7490', color: '#fff', border: 0, borderRadius: 8, padding: '6px 14px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }} onClick={reload}>{AM_NEW_TEXTS.riprova}</button>
        </div>
      ) : null}

      <DataTable
        columns={columns}
        rows={filtered}
        rowKey="id"
        onRowClick={(f) => apriDettaglio(win, f.id)}
        loading={dati.loading}
        error={list && list.length ? null : dati.error}
        onRetry={reload}
        emptyMessage={emptyMessage(list)}
      />
    </div>
  );
}

// Factory (pattern createAmmV3Rateizzazioni)
export function createAmmV3F24(win) {
  let root = null;
  let mountedEl = null;
  let tick = 0;

  function render() {
    const container = win.document.getElementById('amm-page');
    if (!container) throw new Error('#amm-page assente');
    const manomesso = root && mountedEl === container && tick > 0 && !container.querySelector('[data-amm-v3]');
    if (!root || mountedEl !== container || !container.isConnected || manomesso) {
      if (root) { try { root.unmount(); } catch (e) { /* root già ferito/smontato */ } }
      container.replaceChildren();
      root = win.ReactDOM.createRoot(container);
      mountedEl = container;
    }
    tick += 1;
    root.render(<F24View win={win} tick={tick} />);
  }

  function unmount() {
    if (root) {
      try { root.unmount(); } catch (e) { /* già smontato */ }
      root = null;
    }
    mountedEl = null;
  }

  function isMounted() { return !!root; }

  return { render, unmount, isMounted };
}
